import { jsx as _jsx } from "react/jsx-runtime";
/**
 * Chat Context
 *
 * React Context for managing chat state with real-time updates,
 * session management, and error handling
 *
 * @package WooAIChatbot
 * @author Roman Siromskyi <leowebmark@gmail.com>
 */
import { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import chatService, { getSessionId, saveSessionId, clearSessionId } from '../services/chatService';
import { trackChatEvent, trackEcommerce, initFromWordPress } from '../analytics';
import { updateCartCountInHeader } from '../utils/cart';
/**
 * Chat Context
 */
const ChatContext = createContext(undefined);
/**
 * Chat Provider Component
 *
 * Manages chat state with real-time message updates and session handling
 */
export const ChatProvider = ({ children, autoInit = true, initialContext }) => {
    const [messages, setMessages] = useState([]);
    const [sessionId, setSessionId] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [hasMoreHistory, setHasMoreHistory] = useState(false);
    const [isLoadingHistory, setIsLoadingHistory] = useState(false);
    const analyticsInitializedRef = useRef(false);
    const sessionInitializedRef = useRef(false);
    const retryAttemptedRef = useRef(false);
    /**
     * Initialize analytics from WordPress globals
     */
    useEffect(() => {
        if (!analyticsInitializedRef.current) {
            initFromWordPress();
            analyticsInitializedRef.current = true;
        }
    }, []);
    /**
     * Initialize or get existing session
     */
    const initializeSession = useCallback(async () => {
        // Prevent multiple initializations
        if (sessionInitializedRef.current) {
            return;
        }
        sessionInitializedRef.current = true;
        try {
            setLoading(true);
            setError(null);
            // Use chatService.getOrCreateSession which validates stored sessions
            // and creates a new one if missing or expired
            const hadStoredSession = !!getSessionId();
            const currentSessionId = await chatService.getOrCreateSession(initialContext);
            if (!hadStoredSession) {
                // Track new conversation start — external analytics (GA4/Mixpanel/etc.)
                trackChatEvent('conversation_start', {
                    custom: { session_id: currentSessionId },
                });
                // Track in internal DB analytics so the funnel shows accurate counts
                if (currentSessionId) {
                    chatService.trackEvent(currentSessionId, 'conversation_start', {
                        session_id: currentSessionId,
                    });
                }
            }
            setSessionId(currentSessionId);
            // Load history for the session
            if (currentSessionId) {
                try {
                    const history = await chatService.getHistory(currentSessionId);
                    if (history && history.length > 0) {
                        setMessages(history);
                    }
                }
                catch (historyError) {
                    // If history fails, create new session but DON'T clear existing messages
                    console.warn('Failed to load history, creating new session:', historyError);
                    clearSessionId();
                    const newSessionId = await chatService.getOrCreateSession(initialContext);
                    saveSessionId(newSessionId);
                    setSessionId(newSessionId);
                    // Keep existing messages if any, don't reset to empty
                }
            }
        }
        catch (err) {
            const apiError = err;
            setError(apiError);
            console.error('Failed to initialize session:', apiError);
            sessionInitializedRef.current = false; // Allow retry on error
        }
        finally {
            setLoading(false);
        }
    }, [initialContext]);
    /**
     * Initialize session on mount if autoInit is enabled
     */
    useEffect(() => {
        if (autoInit) {
            initializeSession();
        }
    }, [autoInit, initializeSession]);
    /**
     * Execute cart actions immediately via Store API (bypasses React re-render timing).
     * This is the RELIABLE path - runs as soon as SSE meta arrives.
     */
    const executeCartActionsImmediate = useCallback(async (actions) => {
        for (const action of actions) {
            try {
                // Get fresh cart + nonce
                const cartResp = await fetch('/wp-json/wc/store/v1/cart', { credentials: 'same-origin' });
                const freshNonce = cartResp.headers.get('Nonce') || cartResp.headers.get('X-WC-Store-API-Nonce') || '';
                const cartData = await cartResp.json();
                const items = cartData.items || [];
                if (action.action === 'add') {
                    // Add via wc-ajax first, Store API fallback (same as ChatMessage)
                    const formData = new FormData();
                    formData.append('product_id', String(action.product_id));
                    formData.append('quantity', String(action.quantity || 1));
                    if (action.variation_id)
                        formData.append('variation_id', String(action.variation_id));
                    if (action.variation) {
                        for (const [key, val] of Object.entries(action.variation)) {
                            formData.append(key.startsWith('attribute_') ? key : `attribute_${key}`, val);
                        }
                    }
                    const ajaxResp = await fetch('/?wc-ajax=add_to_cart', { method: 'POST', body: formData, credentials: 'same-origin' });
                    const ajaxResult = await ajaxResp.json().catch(() => null);
                    if (!ajaxResult || ajaxResult.error) {
                        // Fallback: Store API
                        const addNonceResp = await fetch('/wp-json/wc/store/v1/cart', { credentials: 'same-origin' });
                        const addNonce = addNonceResp.headers.get('Nonce') || freshNonce;
                        await fetch('/wp-json/wc/store/v1/cart/add-item', {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json', 'Nonce': addNonce },
                            body: JSON.stringify({ id: action.variation_id || action.product_id, quantity: action.quantity || 1 }),
                            credentials: 'same-origin',
                        });
                    }
                    console.log('[WooAI] Immediate add executed:', action.product_id);
                    await updateCartCountInHeader();
                    // Track cart add for analytics funnel
                    try {
                        trackEcommerce('add_to_cart', [{
                                id: String(action.product_id),
                                name: action.product_name || `Product ${action.product_id}`,
                                price: 0,
                                quantity: Number(action.quantity) || 1,
                            }], {
                            label: 'ai_chat',
                            custom: { source: 'ai_chat', session_id: sessionId || undefined },
                        });
                        if (sessionId) {
                            chatService.trackEvent(sessionId, 'cart_added', {
                                product_id: action.product_id,
                                variation_id: action.variation_id || null,
                                quantity: action.quantity || 1,
                                source: 'ai_chat',
                            });
                        }
                    }
                    catch (e) {
                        // Tracking must never break cart logic
                        console.warn('[WooAI] Cart add tracking failed:', e);
                    }
                }
                else if (action.action === 'remove') {
                    // Find item to remove
                    let item = items.find((i) => i.key === action.cart_item_key);
                    if (!item && action.product_id)
                        item = items.find((i) => i.id === action.product_id);
                    if (!item && action.product_name && action.product_name !== 'product') {
                        const name = action.product_name.toLowerCase();
                        item = items.find((i) => (i.name || '').toLowerCase().includes(name) || name.includes((i.name || '').toLowerCase()));
                    }
                    if (!item && items.length === 1)
                        item = items[0];
                    if (item?.key) {
                        await fetch('/wp-json/wc/store/v1/cart/remove-item', {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json', 'Nonce': freshNonce },
                            body: JSON.stringify({ key: item.key }),
                            credentials: 'same-origin',
                        });
                        console.log('[WooAI] Immediate remove executed:', item.name);
                        await updateCartCountInHeader();
                        // Track cart remove for analytics funnel
                        try {
                            trackEcommerce('remove_from_cart', [{
                                    id: String(action.product_id || item.id || '0'),
                                    name: action.product_name || item.name || `Product ${action.product_id}`,
                                    price: 0,
                                    quantity: Number(action.quantity) || 1,
                                }], {
                                label: 'ai_chat',
                                custom: { source: 'ai_chat', session_id: sessionId || undefined },
                            });
                            if (sessionId) {
                                chatService.trackEvent(sessionId, 'cart_removed', {
                                    product_id: action.product_id || null,
                                    variation_id: action.variation_id || null,
                                    quantity: action.quantity || 1,
                                    source: 'ai_chat',
                                });
                            }
                        }
                        catch (e) {
                            // Tracking must never break cart logic
                            console.warn('[WooAI] Cart remove tracking failed:', e);
                        }
                    }
                    else {
                        console.warn('[WooAI] Immediate remove: no item found (' + items.length + ' items)');
                    }
                }
                else if (action.action === 'clear') {
                    let clearNonce = freshNonce;
                    for (const item of items) {
                        if (item.key) {
                            const r = await fetch('/wp-json/wc/store/v1/cart/remove-item', {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/json', 'Nonce': clearNonce },
                                body: JSON.stringify({ key: item.key }),
                                credentials: 'same-origin',
                            });
                            const nextNonce = r.headers.get('Nonce') || clearNonce;
                            if (nextNonce)
                                clearNonce = nextNonce;
                        }
                    }
                    console.log('[WooAI] Immediate clear executed');
                    await updateCartCountInHeader();
                }
                else if (action.action === 'update_quantity') {
                    let item = items.find((i) => i.key === action.cart_item_key);
                    if (!item && action.product_id)
                        item = items.find((i) => i.id === action.product_id);
                    if (!item && action.product_name && action.product_name !== 'product') {
                        const name = action.product_name.toLowerCase();
                        item = items.find((i) => (i.name || '').toLowerCase().includes(name));
                    }
                    if (!item && items.length === 1)
                        item = items[0];
                    if (item?.key) {
                        await fetch('/wp-json/wc/store/v1/cart/update-item', {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json', 'Nonce': freshNonce },
                            body: JSON.stringify({ key: item.key, quantity: action.quantity || 1 }),
                            credentials: 'same-origin',
                        });
                        console.log('[WooAI] Immediate update qty executed:', item.name, 'qty=', action.quantity);
                        await updateCartCountInHeader();
                    }
                }
            }
            catch (e) {
                console.warn('[WooAI] Immediate cart action error:', action.action, e);
            }
        }
        // Refresh cart fragments ONCE after all actions complete, with a delay to allow
        // WC session writes to propagate before get_refreshed_fragments reads them.
        if (actions.length > 0) {
            await new Promise(resolve => setTimeout(resolve, 300));
            try {
                const fragResp = await fetch('/?wc-ajax=get_refreshed_fragments', { method: 'POST', credentials: 'same-origin' });
                const fragData = await fragResp.json().catch(() => null);
                if (fragData?.fragments) {
                    const updateFn = window.updateFragments;
                    if (typeof updateFn === 'function')
                        updateFn(fragData.fragments);
                    const jq = window.jQuery;
                    if (jq) {
                        jq.each(fragData.fragments, (key, value) => jq(key).replaceWith(value));
                        // Trigger multiple WC event variants for maximum theme compatibility
                        jq(document.body).trigger('wc_fragment_refresh');
                        jq(document.body).trigger('wc_fragments_refresh'); // Some themes use this variant
                        jq(document.body).trigger('wc_fragments_refreshed'); // Post-refresh confirmation event
                        jq(document.body).trigger('updated_wc_div'); // Triggers mini-cart refresh
                    }
                }
            }
            catch (e) {
                console.warn('[WooAI] Fragment refresh failed:', e);
            }
            // Donkey theme has a custom cart drawer (div.cart-block) that is NOT registered
            // as a WC fragment. It has its own refresh mechanism via admin-ajax action
            // 'get_refreshed_fragments' + window.updateFragments() function.
            // Feature-gated: runs only if the theme's globals are present (silently no-ops
            // on other themes so plugin remains portable).
            try {
                const donkeyParams = window.donkey_cart_params;
                const updateFragmentsFn = window.updateFragments;
                if (donkeyParams?.ajax_url && typeof updateFragmentsFn === 'function') {
                    const donkeyRes = await fetch(donkeyParams.ajax_url, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                        body: 'action=get_refreshed_fragments',
                        credentials: 'same-origin',
                    });
                    const donkeyData = await donkeyRes.json();
                    if (donkeyData?.success && donkeyData.data?.fragments) {
                        updateFragmentsFn(donkeyData.data.fragments);
                        console.log('[WooAI] Donkey theme cart drawer refreshed via theme ajax action');
                    }
                }
            }
            catch (e) {
                console.warn('[WooAI] Donkey theme cart refresh failed:', e);
            }
        }
    }, [sessionId]);
    /**
     * Send a message to the AI chatbot
     */
    const sendMessage = useCallback(async (message, context) => {
        if (!message.trim()) {
            setError({
                message: 'Message cannot be empty',
                code: 'empty_message',
            });
            return;
        }
        try {
            setLoading(true);
            setError(null);
            // Record start time for response timing
            const messageStartTime = Date.now();
            // Extract image from context if provided
            const imageData = context?.image;
            // Create user message
            const userMessage = {
                id: `user-${Date.now()}`,
                role: 'user',
                content: message,
                timestamp: Date.now(),
                ...(imageData ? { image: imageData } : {}),
            };
            console.log('[WooAI] USER MESSAGE SENT:', message);
            // Add user message to state
            setMessages((prev) => [...prev, userMessage]);
            // Track message sent event (analytics)
            trackChatEvent('message_sent', {
                value: message.length,
                custom: {
                    session_id: sessionId || undefined,
                    message_length: message.length,
                    message: message.substring(0, 200),
                },
            });
            // Create placeholder assistant message for streaming
            const assistantMsgId = `assistant-${Date.now()}`;
            const placeholderMessage = {
                id: assistantMsgId,
                role: 'assistant',
                content: '',
                timestamp: Date.now(),
            };
            // Add placeholder to state immediately (shows typing indicator effect)
            setMessages((prev) => [...prev, placeholderMessage]);
            let response;
            // Track whether any tokens were received during streaming.
            // Used to decide whether to fall back: if tokens arrived, we keep the partial
            // text and skip the retry (avoids duplicate cart actions / double requests).
            let streamingTokensReceived = false;
            // DOUBLE-ADD FIX: track whether cart actions were already executed via SSE meta
            // so the final setMessages does not restore them (which would re-trigger ChatMessage useEffect).
            let cartActionsExecutedViaImmediate = false;
            try {
                // Try streaming first
                response = await chatService.sendMessageStream(message, sessionId || undefined, context, {
                    onToken: (text) => {
                        streamingTokensReceived = true;
                        // Update the assistant message content progressively
                        setMessages((prev) => prev.map((msg) => msg.id === assistantMsgId
                            ? { ...msg, content: msg.content + text }
                            : msg));
                    },
                    onMeta: (meta) => {
                        // Update products/suggestions on the message
                        setMessages((prev) => prev.map((msg) => msg.id === assistantMsgId
                            ? {
                                ...msg,
                                products: meta.products || msg.products,
                                suggestions: meta.suggestions || msg.suggestions,
                                cart_actions: meta.cart_actions || msg.cart_actions,
                                cart: meta.cart || msg.cart,
                            }
                            : msg));
                        // Execute cart actions IMMEDIATELY on meta arrival (don't wait for React re-render).
                        // The useEffect in ChatMessage has timing issues with React 18 batching.
                        if (meta.cart_actions?.length) {
                            cartActionsExecutedViaImmediate = true;
                            executeCartActionsImmediate(meta.cart_actions).catch((e) => console.warn('[WooAI] Immediate cart action failed:', e));
                            // DOUBLE-ADD FIX: clear cart_actions on the message immediately after
                            // executeCartActionsImmediate fires, so ChatMessage's useEffect sees
                            // hasCartActions=false and does NOT execute them a second time.
                            setMessages((prev) => prev.map((msg) => msg.id === assistantMsgId
                                ? { ...msg, cart_actions: [] }
                                : msg));
                        }
                    },
                    onError: (errorMsg) => {
                        console.warn('[WooAI] Streaming error:', errorMsg);
                    },
                });
            }
            catch (streamError) {
                if (streamingTokensReceived) {
                    // Tokens already shown - don't retry to avoid duplicate cart actions.
                    // The partial streamed content is already in the message; just log and continue.
                    console.warn('[WooAI] Streaming error after tokens received, keeping partial response:', streamError);
                    // Build a minimal response from what we have so the rest of the handler works.
                    response = {
                        sessionId: sessionId || '',
                        message: '', // content already set via onToken updates
                        products: [],
                        suggestions: [],
                        tool_results: [],
                        cart_actions: [],
                    };
                }
                else {
                    console.warn('[WooAI] Streaming failed before any tokens, falling back to regular API:', streamError);
                    // Remove the placeholder message since we'll add a proper one
                    setMessages((prev) => prev.filter((msg) => msg.id !== assistantMsgId));
                    // Fall back to regular sendMessage
                    response = await chatService.sendMessage(message, sessionId || undefined, context);
                    // Retry once if response is empty (but not if cart_actions were returned)
                    const hasContent = response?.message?.trim();
                    const hasProducts = response?.products && response.products.length > 0;
                    const hasCartActions = response?.cart_actions && response.cart_actions.length > 0;
                    if (!hasContent && !hasProducts && !hasCartActions) {
                        await new Promise(r => setTimeout(r, 500));
                        response = await chatService.sendMessage(message, sessionId || undefined, context);
                    }
                }
            }
            // Update session ID if it's a new session
            if (response.sessionId && response.sessionId !== sessionId) {
                setSessionId(response.sessionId);
                saveSessionId(response.sessionId);
            }
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            console.log('[WooAI] BOT RESPONSE:', { text: response.message, products: response.products?.length, tool_results: response.tool_results });
            // NON-STREAMING PATH: execute cart_actions immediately (uniform with streaming onMeta path).
            // Only applies when we fell back to sendMessage (placeholder was removed, no streaming was used).
            // cartActionsExecutedViaImmediate is still false here in the non-streaming fallback case.
            if (!cartActionsExecutedViaImmediate && response.cart_actions?.length) {
                cartActionsExecutedViaImmediate = true;
                await executeCartActionsImmediate(response.cart_actions);
                response.cart_actions = []; // prevent ChatMessage useEffect double-execution
            }
            // Determine final message content
            const finalMessage = response.message || '';
            const finalProducts = response.products || [];
            // Final update of assistant message with complete data
            setMessages((prev) => {
                const existing = prev.find((msg) => msg.id === assistantMsgId);
                if (existing) {
                    // Streaming was used - update with final data, keep streamed content if available
                    return prev.map((msg) => msg.id === assistantMsgId
                        ? {
                            ...msg,
                            content: msg.content || finalMessage || 'I apologize, but I couldn\'t process your request. Please try rephrasing your question or ask about specific products.',
                            products: msg.products?.length ? msg.products : finalProducts,
                            suggestions: msg.suggestions || response.suggestions,
                            tool_results: response.tool_results?.map((tr) => ({
                                tool: tr.tool,
                                result: { ...tr },
                            })),
                            metadata: response.metadata,
                            // DOUBLE-ADD FIX: if executeCartActionsImmediate already ran (SSE meta path),
                            // keep cart_actions empty so ChatMessage's useEffect does not execute them again.
                            // If we're in the non-streaming fallback path, use response.cart_actions normally
                            // (ChatMessage useEffect is the only executor in that case).
                            cart_actions: cartActionsExecutedViaImmediate ? [] : (response.cart_actions?.length ? response.cart_actions : (msg.cart_actions || [])),
                            cart: response.cart ? response.cart : msg.cart,
                        }
                        : msg);
                }
                else {
                    // Fallback was used - add new message
                    const fallbackContent = finalMessage.trim() || (finalProducts.length === 0
                        ? 'I apologize, but I couldn\'t process your request. Please try rephrasing your question or ask about specific products.'
                        : finalMessage);
                    return [...prev, {
                            id: assistantMsgId,
                            role: 'assistant',
                            content: fallbackContent,
                            timestamp: Date.now(),
                            suggestions: response.suggestions,
                            products: finalProducts,
                            tool_results: response.tool_results?.map((tr) => ({
                                tool: tr.tool,
                                result: { ...tr },
                            })),
                            metadata: response.metadata,
                            cart_actions: response.cart_actions,
                            cart: response.cart,
                        }];
                }
            });
            // Reset retry flag on success
            retryAttemptedRef.current = false;
            // Calculate response time
            const responseTimeMs = Date.now() - messageStartTime;
            // Track message received event (analytics)
            trackChatEvent('message_received', {
                value: responseTimeMs,
                custom: {
                    session_id: response.sessionId || sessionId || undefined,
                    response_time_ms: responseTimeMs,
                    has_products: !!response.products?.length,
                    product_count: response.products?.length || 0,
                    has_suggestions: !!response.suggestions?.length,
                },
            });
            // Track message sent event (internal API)
            if (response.sessionId) {
                chatService.trackEvent(response.sessionId, 'message_sent', {
                    userMessage: message,
                    hasProducts: !!response.products?.length,
                    hasSuggestions: !!response.suggestions?.length,
                    playbook: response.playbook,
                    topic: response.topic,
                });
            }
        }
        catch (err) {
            const apiError = err;
            console.error('Failed to send message:', apiError);
            // Auto-recovery: reset session and retry once
            if (!retryAttemptedRef.current) {
                retryAttemptedRef.current = true;
                console.warn('[WooAI] Message failed, resetting session and retrying...');
                clearSessionId();
                sessionInitializedRef.current = false;
                try {
                    const newSid = await chatService.getOrCreateSession(initialContext);
                    saveSessionId(newSid);
                    setSessionId(newSid);
                    // Retry the message with new session
                    const retryResponse = await chatService.sendMessage(message, newSid, context);
                    const retryMsg = {
                        id: `assistant-${Date.now()}`,
                        role: 'assistant',
                        content: retryResponse.message || '',
                        timestamp: Date.now(),
                        suggestions: retryResponse.suggestions,
                        products: retryResponse.products || [],
                        metadata: retryResponse.metadata,
                        cart_actions: retryResponse.cart_actions || [],
                    };
                    setMessages((prev) => [...prev, retryMsg]);
                    setError(null);
                    setLoading(false);
                    return;
                }
                catch (retryErr) {
                    console.error('[WooAI] Retry also failed:', retryErr);
                }
            }
            setError(apiError);
            // Add error message from assistant instead of removing user message
            const errorMessage = {
                id: `error-${Date.now()}`,
                role: 'assistant',
                content: 'Sorry, I encountered an error processing your message. Please try again.',
                timestamp: Date.now(),
            };
            setMessages((prev) => [...prev, errorMessage]);
        }
        finally {
            setLoading(false);
        }
    }, [sessionId, initialContext]);
    /**
     * Clear chat history and start new session
     */
    const clearHistory = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            // Try to clear server-side session (non-blocking — UI reset happens regardless)
            if (sessionId) {
                try {
                    await chatService.clearSession(sessionId);
                }
                catch (clearErr) {
                    console.warn('[WooAI] Server session clear failed (non-blocking):', clearErr);
                }
            }
            // Always reset local state
            setMessages([]);
            setSessionId(null);
            // Initialize new session
            await initializeSession();
        }
        catch (err) {
            const apiError = err;
            setError(apiError);
            console.error('Failed to clear history:', apiError);
        }
        finally {
            setLoading(false);
        }
    }, [sessionId, initializeSession]);
    /**
     * Load chat history for current session (initial 20 messages)
     */
    const loadHistory = useCallback(async () => {
        if (!sessionId) {
            console.warn('No session ID available to load history');
            return;
        }
        try {
            setLoading(true);
            setError(null);
            // Load only the most recent 20 messages for initial load
            const history = await chatService.getHistory(sessionId, { limit: 20 });
            setMessages(history);
            // If we got exactly 20 messages, there might be more
            setHasMoreHistory(history.length === 20);
        }
        catch (err) {
            const apiError = err;
            setError(apiError);
            console.error('Failed to load history:', apiError);
            throw apiError;
        }
        finally {
            setLoading(false);
        }
    }, [sessionId]);
    /**
     * Load more (older) chat history
     */
    const loadMoreHistory = useCallback(async () => {
        if (!sessionId || isLoadingHistory || !hasMoreHistory) {
            return;
        }
        try {
            setIsLoadingHistory(true);
            setError(null);
            // Load next 20 older messages
            const olderMessages = await chatService.getHistory(sessionId, {
                limit: 20,
                offset: messages.length
            });
            if (olderMessages.length > 0) {
                // Prepend older messages to the beginning of the array
                setMessages((prev) => [...olderMessages, ...prev]);
                // Update hasMoreHistory based on whether we got a full batch
                setHasMoreHistory(olderMessages.length === 20);
            }
            else {
                setHasMoreHistory(false);
            }
        }
        catch (err) {
            const apiError = err;
            setError(apiError);
            console.error('Failed to load more history:', apiError);
        }
        finally {
            setIsLoadingHistory(false);
        }
    }, [sessionId, messages.length, isLoadingHistory, hasMoreHistory]);
    const value = {
        messages,
        sessionId,
        loading,
        error,
        hasMoreHistory,
        isLoadingHistory,
        sendMessage,
        clearHistory,
        loadHistory,
        loadMoreHistory,
    };
    return (_jsx(ChatContext.Provider, { value: value, children: children }));
};
/**
 * useChat Hook
 *
 * Access chat context
 *
 * @throws {Error} If used outside ChatProvider
 */
export const useChat = () => {
    const context = useContext(ChatContext);
    if (context === undefined) {
        throw new Error('useChat must be used within a ChatProvider');
    }
    return context;
};
